import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useExploreApi } from "@/v2/entities/explore/Explore.api";
import {
  ExploreView,
  CreateExploreView,
} from "@/v2/entities/explore/Explore.model";
import { Filter, isAndFilter } from "@/v2/entities/explore/Explore.filter";
import { ExploreFiltersValue } from "../explore-filter-menu/ExploreFilterMenu";
import { parseFiltersToFilterRequest } from "../explore-filter-menu/explore-filter.utils";
import {
  DisplayOptions,
  AllowedInteractionOrderBy,
  AllowedAggOrderBy,
} from "./explore-display-options/ExploreDisplayOptions.model";
import {
  defaultTicketsColumns,
  defaultConversationsColumns,
  defaultTicketsAggColumns,
  defaultConversationsAggColumns,
} from "./explore-display-options/ExploreDisplayOptions.consts";
import { parseFiltersFromFilterString } from "../explore-filter-menu/explore-filter.utils";
import { SortingState } from "@tanstack/react-table";
import { useExploreCustomFields } from "./useExploreCustomFields";
import { useExploreConversationCustomFieldsForView } from "./useExploreConversationCustomFieldsForView";
import { ExploreColumnType } from "@/v2/entities/explore/columns/ExploreColum.model";

interface UseExploreViewFormProps {
  mode: "create" | "update";
  initialData?: Partial<ExploreView>;
  onSubmit?: (data: CreateExploreView) => Promise<void>;
  onCancel?: () => void;
}

export interface ViewFormData {
  title: string;
  source: "tickets" | "conversations";
}

export interface ViewFormErrors {
  title?: string;
  source?: string;
}

export function useExploreViewForm({
  mode,
  initialData,
  onSubmit,
  onCancel,
}: UseExploreViewFormProps) {
  const navigate = useNavigate();
  const { createView } = useExploreApi();

  const [formData, setFormData] = useState<ViewFormData>({
    title: initialData?.title || "",
    source: (initialData?.source as "tickets" | "conversations") || "tickets",
  });
  const customFields = useExploreCustomFields();
  const conversationCustomFieldsForView =
    useExploreConversationCustomFieldsForView();

  const [errors, setErrors] = useState<ViewFormErrors>({});
  ``;
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState<ExploreFiltersValue>(() => {
    if (mode === "update" && initialData?.filterString) {
      try {
        return parseFiltersFromFilterString(initialData.filterString);
      } catch (error) {
        console.error("Error parsing initial filters:", error);
        return {};
      }
    }
    return {};
  });

  const defaultColumns = useMemo(() => {
    const ticketDynamicColumns = customFields?.map(
      (customField) => customField.id as ExploreColumnType
    );
    const conversationDynamicColumns = conversationCustomFieldsForView?.map(
      (customField) => customField.id as ExploreColumnType
    );

    return formData.source === "conversations"
      ? [...defaultConversationsColumns, ...(conversationDynamicColumns ?? [])]
      : [...defaultTicketsColumns, ...(ticketDynamicColumns ?? [])];
  }, [formData.source, customFields, conversationCustomFieldsForView]);

  const [displayOptions, setDisplayOptions] = useState<DisplayOptions>(() => ({
    orderBy: initialData?.orderBy as AllowedInteractionOrderBy,
    orderDir: initialData?.orderDir || "desc",
    columns: initialData?.columns || defaultColumns,
    groupBy: initialData?.groupBy,
    aggColumns: initialData?.groupBy ? initialData.aggColumns : undefined,
    aggOrderBy: initialData?.aggOrderBy,
    aggOrderDir: initialData?.aggOrderDir,
  }));

  const filter = useMemo<Filter | undefined>(() => {
    const emptyFilter = Object.keys(filters).length === 0;
    if (emptyFilter) {
      return undefined;
    }
    return parseFiltersToFilterRequest(filters);
  }, [filters]);

  const handleFiltersChange = useCallback((newFilters: ExploreFiltersValue) => {
    setFilters(newFilters);
  }, []);

  const handleDisplayOptionsChange = useCallback(
    (newOptions: DisplayOptions) => {
      setDisplayOptions((oldOptions) => {
        if (newOptions.groupBy && !oldOptions.groupBy) {
          newOptions.orderBy = undefined;
          newOptions.orderDir = undefined;
          if (!newOptions.aggOrderBy) {
            newOptions.aggOrderBy = "volume" as AllowedAggOrderBy;
            newOptions.aggOrderDir = "desc";
          }
        } else if (!newOptions.groupBy && oldOptions.groupBy) {
          newOptions.aggOrderBy = undefined;
          newOptions.aggOrderDir = undefined;
          if (!newOptions.orderBy) {
            newOptions.orderBy = "createdAt";
            newOptions.orderDir = "desc";
          }
        }
        return newOptions;
      });
    },
    []
  );

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    if (newSorting.length > 0) {
      const sortItem = newSorting[0];
      setDisplayOptions((prev) => {
        if (prev.groupBy) {
          return {
            ...prev,
            aggOrderBy: sortItem.id as AllowedAggOrderBy,
            aggOrderDir: sortItem.desc ? "desc" : "asc",
          };
        } else {
          return {
            ...prev,
            orderBy: sortItem.id as AllowedInteractionOrderBy,
            orderDir: sortItem.desc ? "desc" : "asc",
          };
        }
      });
    }
  }, []);

  const validateForm = useCallback((): boolean => {
    const newErrors: ViewFormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "View title is required";
    }

    if (!formData.source) {
      newErrors.source = "Source is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleFieldChange = useCallback(
    <K extends keyof ViewFormData>(field: K, value: ViewFormData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors]
  );

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const hasActiveFilters =
        Object.keys(filters).length > 0 &&
        filter != undefined &&
        isAndFilter(filter) &&
        filter.and.length > 0;

      const dataToSubmit: CreateExploreView = {
        title: formData.title,
        source: formData.source,
        filterString: hasActiveFilters ? JSON.stringify(filter) : null,
        orderBy: (displayOptions.orderBy as AllowedInteractionOrderBy) || null,
        orderDir: displayOptions.orderDir as "asc" | "desc" | undefined,
        groupBy: displayOptions.groupBy || null,
        columns: displayOptions.columns,
        aggColumns:
          displayOptions.aggColumns && displayOptions.aggColumns.length > 0
            ? displayOptions.aggColumns
            : null,
        aggOrderBy: displayOptions.aggOrderBy || null,
        aggOrderDir: displayOptions.aggOrderDir || null,
      };

      if (onSubmit) {
        await onSubmit(dataToSubmit);
      } else {
        const newView = await createView(dataToSubmit);
        toast("View created successfully");
        navigate(`/explore/views/${newView.slug}`);
      }
    } catch (error) {
      toast.error("Failed to save view");
    } finally {
      setIsLoading(false);
    }
  }, [
    formData,
    filters,
    filter,
    displayOptions,
    validateForm,
    onSubmit,
    createView,
    navigate,
  ]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/explore/views");
    }
  }, [onCancel, navigate]);

  useEffect(() => {
    if (mode === "create" || !initialData) {
      setDisplayOptions((prev) => ({
        ...prev,
        columns:
          formData.source === "conversations"
            ? defaultConversationsColumns
            : defaultTicketsColumns,
        aggColumns:
          formData.source === "conversations"
            ? defaultConversationsAggColumns
            : defaultTicketsAggColumns,
      }));
    }
  }, [formData.source, mode, initialData]);

  // useEffect(() => {
  //   if (sorting && sorting.length > 0) {
  //     const sortItem = sorting[0];
  //     if (displayOptions.groupBy) {
  //       if (
  //         displayOptions.aggOrderBy !== sortItem.id ||
  //         displayOptions.aggOrderDir !== (sortItem.desc ? "desc" : "asc")
  //       ) {
  //         setDisplayOptions((prev) => ({
  //           ...prev,
  //           aggOrderBy: sortItem.id as AllowedAggOrderBy,
  //           aggOrderDir: sortItem.desc ? "desc" : "asc",
  //         }));
  //       }
  //     } else {
  //       if (
  //         displayOptions.orderBy !== sortItem.id ||
  //         displayOptions.orderDir !== (sortItem.desc ? "desc" : "asc")
  //       ) {
  //         setDisplayOptions((prev) => ({
  //           ...prev,
  //           orderBy: sortItem.id as AllowedInteractionOrderBy,
  //           orderDir: sortItem.desc ? "desc" : "asc",
  //         }));
  //       }
  //     }
  //   }
  // }, [
  //   sorting,
  //   displayOptions.groupBy,
  //   displayOptions.aggOrderBy,
  //   displayOptions.aggOrderDir,
  //   displayOptions.orderBy,
  //   displayOptions.orderDir,
  // ]);

  return {
    formData,
    errors,
    isLoading,
    filters,
    displayOptions,
    filter,
    handleFiltersChange,
    handleDisplayOptionsChange,
    handleFieldChange,
    handleSubmit,
    handleCancel,
    handleSortingChange,
  };
}
